import { RestClient } from './RestClient'

export class TeamClient extends RestClient {
  /**
   * TeamClient constructor
   *
   * @constructor
   */
  constructor () {
    super()

    this.baseUrl = '/teams'
  }

  /**
   * Get an specified team
   *
   * @param {(...number|number[])} id - Requested teams
   *
   * @returns {AxiosPromise}
   */
  get (...id) {
    return this.call('get', `${this.baseUrl}/${this.parseAndJoin(id)}`)
  }

  /**
   * List teams for a user
   *
   * @param {(number|'me')} [id='me'] - User id
   *
   * @returns {AxiosPromise}
   */
  list (id = 'me') {
    return this.call('get', `/users/${id}/teams`)
  }

  /**
   * Remove a team
   *
   * @param {number} id
   * @param {string} authorizationPassword
   *
   * @returns {AxiosPromise}
   */
  delete (id, authorizationPassword) {
    return this.call('delete', `/teams/${id}`, { confirmation_password: authorizationPassword })
  }

  /**
   * Create a new team
   *
   * @param {Object} data - Team info
   *
   * @returns {AxiosPromise}
   */
  create (data) {
    return this.call('post', this.baseUrl, data)
  }

  /**
   * Update the team
   *
   * @param {number} teamId - Team id
   * @param {Object} data - New team data
   *
   * @returns {AxiosPromise}
   */
  update (teamId, data) {
    return this.call('patch', `${this.baseUrl}/${teamId}`, data)
  }

  /**
   * Update the team settings
   *
   * @param {number} teamId - Team id
   * @param {Object} data - New team settings
   *
   * @returns {AxiosPromise}
   */
  updateSettings (teamId, data) {
    return this.call('patch', `${this.baseUrl}/${teamId}/settings`, data)
  }

  /**
   * Upload a new team logo
   *
   * @param {number} teamId - Team id
   * @param {File} logo - Team logo
   *
   * @returns {AxiosPromise}
   */
  uploadLogo (teamId, logo) {
    let data = new FormData()
    data.append('image', logo)

    return this.call('post', `${this.baseUrl}/${teamId}/settings/logo`, data)
  }

  /**
   * Delete a team logo
   *
   * @param {number} teamId - The team id
   *
   * @returns {AxiosPromise}
   */
  deleteLogo (teamId) {
    return this.call('delete', `${this.baseUrl}/${teamId}/settings/logo`)
  }

  /**
   * Upload a new team watermark
   *
   * @param {number} teamId - Team id
   * @param {File} watermark - Team watermark
   *
   * @returns {AxiosPromise}
   */
  uploadWatermark (teamId, watermark) {
    let data = new FormData()
    data.append('image', watermark)

    return this.call('post', `${this.baseUrl}/${teamId}/settings/watermark`, data)
  }

  /**
   * Delete a team watermark
   *
   * @param {number} teamId - The team id
   *
   * @returns {AxiosPromise}
   */
  deleteWatermark (teamId) {
    return this.call('delete', `${this.baseUrl}/${teamId}/settings/watermark`)
  }
  /**
   * Remove member from team
   *
   * @param {number} team - Team id
   * @param {number} user - User id
   *
   * @returns {AxiosPromise}
   */
  removeMember (team, user) {
    return this.call('delete', `/users/${user}/teams/${team}`)
  }

  /**
   * Update the role_id of a team member
   *
   * @param {number} team
   * @param {number} user
   * @param {number} role
   *
   * @returns {AxiosPromise}
   */
  updateMember (team, user, role) {
    return this.call('patch', `/teams/${team}/member/${user}`, { role_id: role })
  }

  /**
   * Create a new address for the specified team
   *
   * @param {number} teamId - Team id
   * @param {Object} data - Address info
   *
   * @returns {AxiosPromise}
   */
  createAddress (teamId, data) {
    return this.call('post', `${this.baseUrl}/${teamId}/addresses`, data)
  }

  /**
   * Update an existing team address
   *
   * @param {number} teamId - Team id
   * @param {number} addressId - Address id
   * @param {Object} data - Address info
   *
   * @returns {AxiosPromise}
   */
  updateAddress (teamId, addressId, data) {
    return this.call('patch', `${this.baseUrl}/${teamId}/addresses/${addressId}`, data)
  }

  /**
   * Delete the specified team addresses
   *
   * @param {number} teamId - Team id
   * @param {(...number|number[])} addressId - Addresses which should be deleted
   *
   * @returns {AxiosPromise}
   */
  destroyAddresses (teamId, ...addressId) {
    return this.call('delete', `${this.baseUrl}/${teamId}/addresses/${this.parseAndJoin(addressId)}`)
  }

  /**
   * Create new bank details for the specified team
   *
   * @param {number} teamId - Team id
   * @param {Object} data - Bank details info
   *
   * @returns {AxiosPromise}
   */
  createBankDetails (teamId, data) {
    return this.call('post', `${this.baseUrl}/${teamId}/bank-details`, data)
  }

  /**
   * Update existing team bank details
   *
   * @param {number} teamId - Team id
   * @param {number} bankDetailsId - Bank details id
   * @param {object} data - Bank details info
   *
   * @returns {AxiosPromise}
   */
  updateBankDetails (teamId, bankDetailsId, data) {
    return this.call('patch', `${this.baseUrl}/${teamId}/bank-details/${bankDetailsId}`, data)
  }

  /**
   * Delete the specified team bank details
   *
   * @param {number} teamId - Team id
   * @param {(...number|number[])} bankDetailsId - Bank details which should be deleted
   *
   * @returns {AxiosPromise}
   */
  destroyBankDetails (teamId, ...bankDetailsId) {
    return this.call('delete', `${this.baseUrl}/${teamId}/bank-details/${this.parseAndJoin(bankDetailsId)}`)
  }

  /**
   * Create a new email address for the specified team
   *
   * @param {number} teamId - Team id
   * @param {Object} data - Email address info
   *
   * @returns {AxiosPromise}
   */
  createEmailAddress (teamId, data) {
    return this.call('post', `${this.baseUrl}/${teamId}/email-addresses`, data)
  }

  /**
   * Update an existing team email address
   *
   * @param {number} teamId - Team id
   * @param {number} emailAddressId - Email address id
   * @param {Object} data - Email address info
   *
   * @returns {AxiosPromise}
   */
  updateEmailAddress (teamId, emailAddressId, data) {
    return this.call('patch', `${this.baseUrl}/${teamId}/email-addresses/${emailAddressId}`, data)
  }

  /**
   * Delete the specified team email addresses
   *
   * @param {number} teamId - Team id
   * @param {(...number|number[])} emailAddressId - Email addresses which should be deleted
   *
   * @returns {AxiosPromise}
   */
  destroyEmailAddresses (teamId, ...emailAddressId) {
    return this.call('delete', `${this.baseUrl}/${teamId}/email-addresses/${this.parseAndJoin(emailAddressId)}`)
  }

  /**
   * Create a new phone number for the specified team
   *
   * @param {number} teamId - Team id
   * @param {Object} data - Phone number info
   *
   * @returns {AxiosPromise}
   */
  createPhoneNumber (teamId, data) {
    return this.call('post', `${this.baseUrl}/${teamId}/phone-numbers`, data)
  }

  /**
   * Update an existing team phone number
   *
   * @param {number} teamId - Team id
   * @param {number} phoneNumberId - Phone number id
   * @param {Object} data - Phone number info
   *
   * @returns {AxiosPromise}
   */
  updatePhoneNumber (teamId, phoneNumberId, data) {
    return this.call('patch', `${this.baseUrl}/${teamId}/phone-numbers/${phoneNumberId}`, data)
  }

  /**
   * Delete the specified team phone numbers
   *
   * @param {number} teamId - Team id
   * @param {(...number|number[])} phoneNumberId - Phone numbers which should be deleted
   *
   * @returns {AxiosPromise}
   */
  destroyPhoneNumbers (teamId, ...phoneNumberId) {
    return this.call('delete', `${this.baseUrl}/${teamId}/phone-numbers/${this.parseAndJoin(phoneNumberId)}`)
  }

  /**
   * Confirm the team deletion
   *
   * @param {String} token - Token used to authorize the confirmation
   *
   * @returns {AxiosPromise}
   */
  confirmDeletion (token) {
    return this.call('post', '/teams/delete/confirm', { token })
  }

  /**
   * Upload a new PDF template
   *
   * @param {number} teamId - Team id
   * @param {File} template - PDF template
   *
   * @returns {AxiosPromise}
   */
  uploadPDFTemplate (teamId, template) {
    let data = new FormData()
    data.append('template', template)

    return this.call('post', `${this.baseUrl}/${teamId}/settings/pdf-template`, data)
  }

  /**
   * Delete a PDF template
   *
   * @param {number} teamId - The team id
   *
   * @returns {AxiosPromise}
   */
  deletePDFTemplate (teamId) {
    return this.call('delete', `${this.baseUrl}/${teamId}/settings/pdf-template`)
  }

  /**
   * Preview a PDF template
   *
   * @param {number} teamId - The team id
   *
   * @returns {AxiosPromise}
   */
  previewPDFTemplate (teamId) {
    return this.call('get', `${this.baseUrl}/${teamId}/settings/pdf-template/preview`, {}, {
      'Accept': 'application/pdf'
    }, {
      responseType: 'arraybuffer'
    })
  }
}
